/* istanbul ignore file */

import React, { useState, useRef, useEffect } from 'react';
import { ListGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import { FormGroup, FormControlLabel, TextField } from '@material-ui/core';
import styles from './Onboarding.module.css';
import { updateInStorage } from '../../utils/get-location/location-from-storage';
import {
  isMultiSelectQuestion, isUserInputNumberQuestion, isSingleListMultiList, isAudioInputQuestion, isUserInputTextQuestion
} from './utils';
import useTranslation from '../../hooks/use-translation';
import { trimUpperCase } from '../../utils/utils';
import UserRisk from './UserRisk';
import AudioInput from './audioComponent/Timer';
import { validationsEnum } from './enums.js';

const renderSingleSelect = ({ question, enabled, onAnswerSelected }) => (
  <ListGroup>
    {question.options.map(item => (
      <ListGroup.Item
        className="list-item-question"
        variant="light"
        style={{ outline: 'none', boxShadow: 'none' }}
        key={question.question_id + item.id}
        action
        disabled={!enabled}
        onClick={onAnswerSelected(item)}
      >
        {item.preferred_term}
      </ListGroup.Item>
    ))}
  </ListGroup>
);

const renderSingleSelectMultiList = ({
  question, enabled, onAnswerSelected, t
}) => (
    <div style={{ fontFamily: 'jioType-Medium' }}>
      <div style={{ color: '#7D7D7D', fontSize: '0.875em', marginBottom: '0.625em' }}>{t('MY_FAMILY')}</div>
      <ListGroup>
        {
          question.familyOptions.map(item => (
            <ListGroup.Item
              className="list-item-question"
              variant="light"
              style={{ outline: 'none', boxShadow: 'none' }}
              key={question.question_id + item.id}
              action
              onClick={onAnswerSelected(item)}
              disabled={!enabled}
            >
              <div style={{
                display: 'flex',
                flexFlow: 'row',
                justifyContent: 'space-between'
              }}
              >
                <div>
                  {t(`RELATION_${trimUpperCase(item.profile_relation)}`)}
                </div>
                <div style={{ color: '#7D7D7D' }}>{item.preferred_term}</div>
              </div>
              <div style={{
                marginTop: '10px',
              }}
              >
                <UserRisk item={item} riskLayout='flexRow' />
              </div>
            </ListGroup.Item>
          ))
        }
      </ListGroup>
      <Link
        to="/create-profile/relative?ref=workflow?flow=profileskip"
        style={{
          fontSize: '0.875em',
          fontFamily: 'jioType-Medium',
          color: '#214796',
          textAlign: 'right'
        }}
      >
        <div style={{ marginBottom: '1.78em', marginTop: '0.25em' }}>{t('ADD_MEMBER_PROFILE')}</div>
      </Link>
      <div style={{
        color: '#7D7D7D', fontSize: '0.875em', marginBottom: '0.625em', marginTop: ''
      }}
      >
        {t('OTHERS')}
      </div>
      <ListGroup>
        {question.options.map(item => (
          <ListGroup.Item
            className="list-item-question"
            variant="light"
            style={{ outline: 'none', boxShadow: 'none' }}
            key={question.question_id + item.id}
            action
            disabled={!enabled}
            onClick={onAnswerSelected(item)}
          >
            {item.preferred_term}
          </ListGroup.Item>
        ))}
      </ListGroup>
    </div >
  );

const onOptionChange = (event, selectionOptions, clear_others, question, optionId, checked, setChecked) => {
  if (clear_others) {
    selectionOptions.current.splice(0, selectionOptions.current.length);
  } else if (selectionOptions.current.length === 1) {
    for (let i = 0; i < question.options.length; i += 1) {
      const optionItem = question.options[i];
      if (optionItem.clear_others) {
        if (selectionOptions.current.includes(optionItem.id)) {
          setChecked({
            ...checked,
            [optionItem.id]: event.target.checked
          });
          if (selectionOptions.current.length > 0) { selectionOptions.current.splice(0, 1); }
        }
        break;
      }
    }
  }
  setChecked({
    ...checked,
    [optionId]: event.target.checked
  });

  if (event.target.checked) {
    selectionOptions.current.push(event.target.value);
  } else {
    selectionOptions.current.splice(selectionOptions.current.findIndex(ansId => ansId === event.target.value), 1);
  }
};

const UserInputNumber = ({ question, onAnswerSelected, t, selectionOptions }) => {
  const [valueState, setValueState] = useState('');

  const validateInput = (input, validationsArray=[]) => {
    if(!input || isNaN(Number(input))){
      return false;
    }
    if(Array.isArray(validationsArray) && validationsArray.length){
      for(let validationKey = 0; validationKey<validationsArray.length; validationKey++){
        const validation = validationsArray[validationKey];
        if(validation.type == validationsEnum.MINIMUM_VALUE && Number(input) < Number(validation.value)){
          return false;
        }    
        if(validation.type == validationsEnum.MAXIMUM_VALUE && Number(input) > Number(validation.value)){
          return false;
        }    
        if(validation.type == validationsEnum.ALLOW_FLOAT && validation.value === "false" && Number(input) !== parseInt(input, 10)){
          return false;
        }    
        if(validation.type == validationsEnum.IS_BOOLEAN && validation.value === "true" && !(Number(input) == 1 || Number(input) == 0) ){
          return false;
        }        
        if(validation.type == validationsEnum.MINIMUM_LENGTH && input.length < Number(validation.value) ){
          return false;
        }        
        if(validation.type == validationsEnum.MAXIMUM_LENGTH && input.length > Number(validation.value) ){
          return false;
        }
        if(validation.type == validationsEnum.REGEX && validation.value){
          const regexTester = new RegExp(validation.value);
          if(!regexTester.test(input)){
            return false;
          }
        }
      }
    }
    return true;
  }
  const onTextChange = event => {
    const { value } = event.target;
    return setValueState(value);
  };

  const onNextClick = () => {
    const valid = validateInput(valueState, question.validations)
    if (!valid) {
      const error = question.validation_error_msg ||  t("QUESTION_ANSWERING_ADVISE") ;
      alert(error);
      return;
    }
    updateInStorage(question.question_id, valueState);
    selectionOptions.current.push(valueState);
    onAnswerSelected(selectionOptions)();
  }

  return (
    <>
      <div style={{
        width: '100%'
      }}
      >
        <TextField
          value={valueState}
          margin="normal"
          variant="outlined"
          type="number"
          onChange={onTextChange}
          inputProps={{
            style: {
              padding: '1em 0.625em'
            }
          }}
        //   placeholder={t('ADD_PLACEHOLDER')}
        />

      </div>
      <div>
        <Button
          style={{ width: '7%', alignSelf: 'flex-end' }}
          onClick={onNextClick}
          variant="contained"
          color="primary"
        >
          {t('NEXT')}
        </Button>
      </div>
    </>
  );
}

const UserInputText = ({ question, onAnswerSelected, t, selectionOptions }) => {
  const [valueState, setValueState] = useState('');

  const validateInput = (input, validationsArray=[]) => {
    if(!input){
      return false;
    }
    if(Array.isArray(validationsArray) && validationsArray.length){
      for(let validationKey = 0; validationKey<validationsArray.length; validationKey++){
        const validation = validationsArray[validationKey];
        if(validation.type == validationsEnum.MINIMUM_LENGTH && input.length < Number(validation.value) ){
          return false;
        }        
        if(validation.type == validationsEnum.MAXIMUM_LENGTH && input.length > Number(validation.value) ){
          return false;
        }
        if(validation.type == validationsEnum.REGEX && validation.value){
          const regexTester = new RegExp(validation.value);
          if(!regexTester.test(input)){
            return false;
          }
        }
      }
    }
    return true;
  }
  const onTextChange = event => {
    const { value } = event.target;
    return setValueState(value);
  };

  const onNextClick = () => {
    const valid = validateInput(valueState, question.validations) 
    if (!valid) {
      const error = question.validation_error_msg || t('QUESTION_ANSWERING_ADVISE')
      alert(error);
      return;
    }
    updateInStorage(question.question_id, valueState);
    selectionOptions.current.push(valueState);
    onAnswerSelected(selectionOptions)();
  }

  return (
    <>
      <div style={{
        width: '100%'
      }}
      >
        <TextField
          value={valueState}
          margin="normal"
          variant="outlined"
          type="text"
          onChange={onTextChange}
          inputProps={{
            style: {
              padding: '1em 0.625em'
            }
          }}
        //   placeholder={t('ADD_PLACEHOLDER')}
        />

      </div>
      <div>
        <Button
          style={{ width: '7%', alignSelf: 'flex-end' }}
          onClick={onNextClick}
          variant="contained"
          color="primary"
        >
          {t('NEXT')}
        </Button>
      </div>
    </>
  );
}

const renderMultiSelect = ({
  question, onAnswerSelected, t, selectionOptions, checked, setChecked
}) => (
    <div style={{
      display: 'flex',
      flexFlow: 'column'
    }}
    >
      <FormGroup>
        {question.options.map(item => (
          <FormControlLabel
            key={question.question_id + item.id}
            control={(
              <Checkbox
                color="primary"
                name={item.name}
                onChange={e => onOptionChange(e, selectionOptions, item.clear_others, question, item.id, checked, setChecked)}
                value={item.id}
                checked={selectionOptions.current.includes(item.id)}
              />
            )}
            label={item.preferred_term}
          />
        ))}
      </FormGroup>
      <Button
        style={{ width: '7%', alignSelf: 'flex-end' }}
        onClick={onAnswerSelected(selectionOptions.current)}
        variant="contained"
        color="primary"
      >
        {t('NEXT')}
      </Button>
    </div>
  );

const renderAudioInput = ({ question, onAnswerSelected, t, selectionOptions }) => (
  <AudioInput
    question={question}
    onSubmit={apiResponse => {
      // console.log('apiResponse', apiResponse);
      onAnswerSelected(apiResponse)();
    }}
    selectionOptions={selectionOptions}
  />
);

const renderCMSOptions = ({
  question, enabled, onAnswerSelected, t, selectionOptions, checked, setChecked
}) => {
  if (isMultiSelectQuestion(question)) {
    return renderMultiSelect({
      question, onAnswerSelected, t, selectionOptions, checked, setChecked
    });
  }

  if (isUserInputNumberQuestion(question)) {
    return <UserInputNumber question={question} onAnswerSelected={onAnswerSelected} t={t} selectionOptions={selectionOptions} />
  }
  
  if (isUserInputTextQuestion(question)) {
    return <UserInputText question={question} onAnswerSelected={onAnswerSelected} t={t} selectionOptions={selectionOptions} />
  }

  if (isSingleListMultiList(question)) {
    return renderSingleSelectMultiList({
      question, enabled, onAnswerSelected, t
    });
  }

  if (isAudioInputQuestion(question)) {
    return renderAudioInput({
      question, onAnswerSelected, t, selectionOptions
    });
  }

  return renderSingleSelect({ question, enabled, onAnswerSelected });
};


export default ({
  question, enabled, onAnswerSelected
}) => {
  if (!question) {
    return <></>;
  }
  const [checked, setChecked] = useState({});

  const selectionOptions = useRef([]);
  const { t } = useTranslation();
  return (
    <div className={styles.card}>
      <div>
        <div className={styles.questionTitle}>
          {question.question_text}
        </div>
        {renderCMSOptions({
          question, enabled, onAnswerSelected, t, selectionOptions, checked, setChecked
        })}
      </div>
    </div>
  );
};
